import React, { useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const DEFAULT_CONFIRM_TEXT = 'Okay';

/**
 * Lightweight message dialog: title, description, primary action (defaults to "Okay"), and dismiss (X, backdrop, Escape).
 *
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {string} title
 * @param {React.ReactNode} description
 * @param {string} [confirmButtonText='Okay']
 * @param {string} [className] Optional extra class on the overlay (e.g. z-index)
 */
function MessageDialogModal({
  open,
  onClose,
  title,
  description,
  confirmButtonText = DEFAULT_CONFIRM_TEXT,
  overlayClassName = 'z-[12050]',
}) {
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      role="presentation"
      className={`fixed inset-0 flex items-center justify-center bg-black/50 p-4 ${overlayClassName}`}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-md rounded-xl border border-gray-200 bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          aria-label="Close dialog"
        >
          <X className="h-5 w-5" aria-hidden />
        </button>
        <div className="px-5 pt-5 pb-4 pr-14">
          <h2 id={titleId} className="text-base font-semibold text-gray-900 sm:text-lg">
            {title}
          </h2>
          <div className="mt-3 text-sm leading-relaxed text-gray-600">{description}</div>
        </div>
        <div className="border-t border-gray-100 px-5 py-3 sm:py-4">
          <button
            type="button"
            onClick={onClose}
            className="btn-primary w-full rounded-md px-4 py-2.5 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto sm:min-w-[7rem]"
          >
            {confirmButtonText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default MessageDialogModal;
